<template>
  <div id="seoul-trade-line-chart" class="apt-chart">
    <div class="title">최근 10년 거래량 추이</div>
    <div class="chart-area">
      <LineChart
        class="chart"
        v-if="chartData !== null"
        :data="chartData"
        :options="chartOptions"
      />
      <div v-else class="chart-skeleton">그래프 불러오는 중…</div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import UtilMixin from "@/common/mixins/UtilMixin";
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Filler,
  ChartData,
  ChartOptions,
} from "chart.js";
import { SeoulTradeStat, SeoulTradeStatRequest } from "../../class";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Filler
);

const COUNT_COLOR = "#4a9fe0";
const COUNT_FILL = "rgba(74, 159, 224, 0.14)";
const AMOUNT_COLOR = "#e0a53a";
const TICK = "#8a9099";
const GRID = "rgba(128, 128, 128, 0.14)";

export default defineComponent({
  name: "SeoulTradeStat10Year",
  components: {
    LineChart: Line,
  },
  setup() {
    return {};
  },

  data() {
    return {
      chartData: null as ChartData<"line"> | null,
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            position: "top" as const,
            labels: { color: TICK, usePointStyle: true, boxWidth: 8, padding: 16 },
          },
          tooltip: {
            callbacks: {
              label: (context: any) => {
                const label = context.dataset.label || "";
                const value = context.raw;
                if (label.includes("금액")) return `${label}: ${value}억`;
                return `${label}: ${Number(value).toLocaleString()}건`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { color: GRID },
            ticks: { color: TICK },
          },
          y1: {
            type: "linear",
            position: "left",
            grid: { color: GRID },
            ticks: { color: TICK },
          },
          y2: {
            type: "linear",
            position: "right",
            grid: { drawOnChartArea: false },
            ticks: {
              color: TICK,
              callback: function (value: any) {
                return `${value}억`;
              },
            },
          },
        },
      } as ChartOptions<"line">,
    };
  },
  mixins: [UtilMixin],
  computed: {
    //
  },
  methods: {
    async fetchSeoulTrade() {
      const now = new Date();
      const start = new Date(now.getFullYear() - 10, 0);

      const formatYyyymm = (date: Date) => {
        const yyyy = date.getFullYear();
        const mm = (date.getMonth() + 1).toString().padStart(2, "0");
        const dd = date.getDate().toString().padStart(2, "0");
        return `${yyyy}${mm}${dd}`;
      };

      const startYm = formatYyyymm(start);
      const endYm = formatYyyymm(now);
      const groupBy = "YEAR";

      const request = new SeoulTradeStatRequest(startYm, endYm, groupBy);

      const res = await this.getApi().apt().getSeoulTradeStats(request);
      if (!Array.isArray(res)) return;

      const labels: string[] = [];
      const totalCountData: number[] = [];
      const totalAvgAmountData: (number | null)[] = [];

      res.forEach((item: SeoulTradeStat) => {
        const { year, totalCount, totalAvgAmount } = item;
        labels.push(`${year}`);
        totalCountData.push(totalCount);
        totalAvgAmountData.push(totalAvgAmount ?? null);
      });

      this.chartData = {
        labels: labels,
        datasets: [
          {
            label: "전체 거래 수",
            data: totalCountData,
            borderColor: COUNT_COLOR,
            backgroundColor: COUNT_FILL,
            borderWidth: 2.5,
            fill: true,
            tension: 0.4,
            cubicInterpolationMode: "monotone",
            pointRadius: 0,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: COUNT_COLOR,
            yAxisID: "y1",
          },
          {
            label: "전체 평균 거래금액",
            data: totalAvgAmountData,
            borderColor: AMOUNT_COLOR,
            backgroundColor: AMOUNT_COLOR,
            borderWidth: 2.5,
            fill: false,
            tension: 0.4,
            cubicInterpolationMode: "monotone",
            pointRadius: 0,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: AMOUNT_COLOR,
            yAxisID: "y2",
          },
        ],
      };
    },
  },
  mounted() {
    this.fetchSeoulTrade();
  },
});
</script>
